import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Typography } from 'interfaces/typography';
import { 
    loadPlatformDispositions,
    selectIsLoading,
    selectIsLoaded,
    selectError
} from 'lib/redux/settings/platformDispositionsSlice';
import { AddNewDisposition } from './dispositions/AddNewDisposition';
import { CurrentDispositionsList } from './dispositions/CurrentDispositionsList';
import { QuickAddPanel } from './dispositions/QuickAddPanel';
import { SaveControls } from './dispositions/SaveControls';
import { SavedDispositionsDisplay } from './dispositions/SavedDispositionsDisplay';

export function DispositionsControl() {
    const dispatch = useDispatch();
    const platform = useSelector((state: RootState) => state.autodialerSettings.selectedPlatform);
    const isLoading = useSelector(selectIsLoading(platform!));
    const isLoaded = useSelector(selectIsLoaded(platform!));
    const error = useSelector(selectError);
    useEffect(() => {
        if (platform && !isLoaded && !isLoading) {
            loadPlatformDispositions(dispatch, platform);
        }
    }, [platform, isLoaded, isLoading, dispatch]);

    if (!platform) {
        return null;
    }

    // Show loading spinner if not loaded yet
    if (!isLoaded && isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <Typography variant="mediumParagraph" color="#6B7280" className="ml-3">
                    Loading dispositions...
                </Typography>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <Typography variant="mediumParagraph" fontWeight="600" color="#1F2937" className="mb-2">
                    Platform Dispositions
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280">
                    Manage the disposition options available for this platform. Add individual dispositions or choose from common options.
                </Typography>
            </div>

            {/* Error Display */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <Typography variant="smallParagraph" color="#DC2626">
                        {error}
                    </Typography>
                </div>
            )}

            {/* Main Content Area - Side by Side Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Current Dispositions Management (2/3 width) */}
                <div className="lg:col-span-2 space-y-4">
                    <AddNewDisposition />
                    <CurrentDispositionsList />
                </div>

                {/* Right Column - Quick Add Panel (1/3 width) */}
                <QuickAddPanel />
            </div>

            {/* Save/Reset/Clear Controls */}
            <SaveControls />

            {/* Saved Dispositions Display */}
            <SavedDispositionsDisplay />
        </div>
    );
}